import truncate from 'lodash/truncate';

import {
  ChartTooltip,
  ChartTooltipComponentProps,
  DataPopper,
  DataPopperRow,
  Pie,
  PieChartContainer,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';
import { dashesOrNum, formatCurrency, getCurrencyPrecision } from '@cast/utils';

const PIE_CHART_RADIUS = 130;

// Use data tokens so colors adapt correctly between light and dark modes
const PIE_FALLBACK_COLORS = [
  tokens.theme.data.blue500,
  tokens.theme.data.green500,
  tokens.theme.data.yellow500,
  tokens.theme.data.red500,
  tokens.theme.data.indigo500,
  tokens.theme.data.red300,
  tokens.theme.data.aqua500,
  tokens.theme.data.green300,
];

const TooltipContent = ({
  payload,
}: ChartTooltipComponentProps<Record<string, unknown>, number>) => {
  if (!payload?.length) {
    return null;
  }

  return (
    <DataPopper size="small">
      {payload.map((entry, index) => (
        <DataPopperRow
          key={index}
          indicator={{
            color: entry.fill ?? PIE_FALLBACK_COLORS[0],
            variant: 'filled',
          }}
          left={
            <Typography variant="P10R" component="span">
              {truncate(String(entry.name ?? ''), { length: 20 })}
            </Typography>
          }
          right={
            <Typography variant="P10R">
              {String(
                dashesOrNum({
                  value: entry.value,
                  mapNumber: (num) =>
                    formatCurrency(num, {
                      maximumFractionDigits: getCurrencyPrecision(num),
                    }),
                })
              )}
            </Typography>
          }
        />
      ))}
    </DataPopper>
  );
};

const Legend = ({
  data,
  nameKey,
}: {
  data: Record<string, unknown>[];
  nameKey: string;
}) => (
  <Stack
    direction="row"
    flexWrap="wrap"
    justifyContent="center"
    gap={8}
    sx={{ px: 12, pb: 12 }}
  >
    {data.map((entry, i) => (
      <Stack key={i} direction="row" alignItems="center" gap={4}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '2px',
            flexShrink: 0,
            backgroundColor:
              PIE_FALLBACK_COLORS[i % PIE_FALLBACK_COLORS.length],
          }}
        />
        <Typography
          variant="P10R"
          color={tokens.theme.fg.structural.primary}
          data-testid="pie-legend-item"
        >
          {truncate(String(entry[nameKey] ?? ''), { length: 20 })}
        </Typography>
      </Stack>
    ))}
  </Stack>
);

export const PieChartArtifact = ({
  title,
  data,
  dataKey,
  nameKey,
}: {
  title?: string;
  data: Record<string, unknown>[];
  dataKey: string;
  nameKey: string;
}) => {
  const chartData = data.map((entry, i) => ({
    ...entry,
    fill: PIE_FALLBACK_COLORS[i % PIE_FALLBACK_COLORS.length],
    name: String(entry[nameKey] ?? ''),
  }));

  return (
    <Box
      sx={{
        mt: 8,
        border: '1px solid',
        borderColor: tokens.theme.border.structural.neutral,
        borderRadius: '8px',
        overflow: 'hidden',
      }}
    >
      {title && (
        <Typography
          variant="P12B"
          sx={{
            display: 'block',
            p: 12,
            pb: 0,
            color: tokens.theme.fg.structural.primary,
          }}
        >
          {title}
        </Typography>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 12 }}>
        <PieChartContainer data={chartData} radius={PIE_CHART_RADIUS}>
          <Pie dataKey={dataKey} />
          <ChartTooltip TooltipComponent={TooltipContent} />
        </PieChartContainer>
      </Box>
      <Legend data={data} nameKey={nameKey} />
    </Box>
  );
};
